import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import {
  Button,
  Card,
  DatePicker,
  Empty,
  Input,
  Segmented,
  Select,
  Space,
  Table
} from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import PageLoader from '../../components/Layouts/PageLoader'
import PaginationContainer from '../../components/Forms/Pagination'
import {
  errorNotification,
  formatUrl,
  getRequest,
  trackingAccountUrl,
  trackingLinesUrl,
  trackingPricingUrl,
  trackingShipmentsUrl
} from '../../config'
import AccountBanner from './AccountBanner'
import { shipmentColumns } from './shipmentColumns'
import {
  ATTENTION,
  CHANNEL_LABELS,
  STATUS_LABELS,
  attentionLabel,
  buildShipmentQuery,
  channelLabel,
  errorSentence,
  formatDateTime
} from './utils'

const { RangePicker } = DatePicker

const VIEW_OPTIONS = [
  { value: 'attention', label: 'Needs attention' },
  { value: 'all', label: 'All shipments' }
]

const STATUS_OPTIONS = Object.keys(STATUS_LABELS).map(value => ({
  value,
  label: STATUS_LABELS[value]
}))

const ATTENTION_OPTIONS = Object.keys(ATTENTION).map(value => ({
  value,
  label: attentionLabel(value)
}))

const CHANNEL_OPTIONS = Object.keys(CHANNEL_LABELS).map(value => ({
  value,
  label: channelLabel(value)
}))

// Two values rather than a checkbox, because "updates off" and "updates on" are both questions
// somebody asks: a run of the first in one week is the signal that the updates themselves are
// wrong, and the second is the normal population.
const UPDATES_OPTIONS = [
  { value: 'true', label: 'Updates stopped' },
  { value: 'false', label: 'Updates on' }
]

// The worklist. It opens on what needs doing rather than on everything, and it never re-sorts the
// API's rows: the server already ranks open alerts by urgency and then by age, and a client-side
// sort would quietly undo that.
const ShipmentsList = ({ history }) => {
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [meta, setMeta] = useState({ previous: null, next: null, count: 0 })
  const [view, setView] = useState('attention')
  // Drafts: what is typed into the filter bar but not yet applied. `applied` is what the rows on
  // screen actually answer to, so paging and Reload keep asking the same question.
  const [status, setStatus] = useState(undefined)
  const [attention, setAttention] = useState(undefined)
  const [line, setLine] = useState(undefined)
  const [channel, setChannel] = useState(undefined)
  const [updatesStopped, setUpdatesStopped] = useState(undefined)
  const [range, setRange] = useState(null)
  const [search, setSearch] = useState('')
  const [applied, setApplied] = useState({ view: 'attention' })
  const [lines, setLines] = useState([])
  const [account, setAccount] = useState(null)
  // `undefined` until the request comes back: the banner must not claim "no price" while the
  // pricing call is still in flight.
  const [pricing, setPricing] = useState(undefined)
  const [loadedAt, setLoadedAt] = useState(null)

  // A `url` is only ever a next/previous link the server handed back — it already carries the
  // filters and the page, so it goes through `formatUrl` and nothing else.
  const load = async (filters, url = null) => {
    if (!url) setApplied(filters)
    const response = await getRequest(
      url ? formatUrl(url) : trackingShipmentsUrl + buildShipmentQuery(filters),
      'shipments',
      setLoading
    )
    if (response.success) {
      setRows(response.data.results || response.data)
      setMeta({ ...response.data })
      setLoadedAt(new Date().toISOString())
    } else {
      // The rows already on screen stay: a failed refresh is not a reason to empty the worklist.
      errorNotification(errorSentence(response, 'The shipments could not be loaded.'))
    }
  }

  useEffect(() => {
    load({ view: 'attention' })
    // The filter's options and the setup banner. These three are best-effort: a failure leaves the
    // line filter empty and the banner silent, and the helper has already said so.
    getRequest(trackingLinesUrl, 'shipping lines', () => {}).then(response => {
      if (response.success) setLines(response.data.results || response.data)
    })
    getRequest(trackingAccountUrl, 'ShipsGo account', () => {}).then(response => {
      if (response.success) setAccount(response.data)
    })
    getRequest(trackingPricingUrl('GH'), 'price', () => {}).then(response => {
      if (response.success) setPricing(response.data)
    })
    // eslint-disable-next-line
  }, [])

  const drafts = nextView => ({
    view: nextView === undefined ? view : nextView,
    status,
    attention,
    line,
    channel,
    updatesStopped,
    range,
    search
  })

  const changeView = nextView => {
    setView(nextView)
    load(drafts(nextView))
  }

  const applyFilters = () => load(drafts())

  const clearFilters = () => {
    setStatus(undefined)
    setAttention(undefined)
    setLine(undefined)
    setChannel(undefined)
    setUpdatesStopped(undefined)
    setRange(null)
    setSearch('')
    load({ view })
  }

  const empty =
    view === 'attention' &&
    !status &&
    !attention &&
    !line &&
    !channel &&
    !updatesStopped &&
    !range &&
    !search ? (
      <Empty description="Nothing needs attention.">
        <Button onClick={() => changeView('all')}>Show all shipments</Button>
      </Empty>
    ) : (
      <Empty description="No shipments match these filters." />
    )

  return (
    <PageLoader className="container" loading={loading}>
      <Card
        title={`Shipments (${meta.count || 0})`}
        extra={
          <Space>
            {loadedAt ? (
              <span style={{ opacity: 0.65 }}>{`loaded ${formatDateTime(loadedAt)}`}</span>
            ) : null}
            <Button icon={<ReloadOutlined />} onClick={() => load(applied)}>
              Reload
            </Button>
          </Space>
        }
      >
        <AccountBanner account={account} pricing={pricing} />

        <Segmented
          options={VIEW_OPTIONS}
          value={view}
          onChange={changeView}
          style={{ marginBottom: 16 }}
        />

        <Space wrap style={{ marginBottom: 16 }}>
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 180 }}
            value={status}
            onChange={setStatus}
            options={STATUS_OPTIONS}
          />
          <Select
            allowClear
            placeholder="Attention"
            style={{ width: 200 }}
            value={attention}
            onChange={setAttention}
            options={ATTENTION_OPTIONS}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="children"
            placeholder="Line"
            style={{ width: 200 }}
            value={line}
            onChange={setLine}
          >
            {lines.map(row => (
              <Select.Option key={row.id} value={row.id}>
                {row.name}
              </Select.Option>
            ))}
          </Select>
          <Select
            allowClear
            placeholder="Channel"
            style={{ width: 150 }}
            value={channel}
            onChange={setChannel}
            options={CHANNEL_OPTIONS}
          />
          <Select
            allowClear
            placeholder="WhatsApp updates"
            style={{ width: 180 }}
            value={updatesStopped}
            onChange={setUpdatesStopped}
            options={UPDATES_OPTIONS}
          />
          <RangePicker
            value={range}
            onChange={setRange}
            placeholder={['Created from', 'To']}
          />
          <Input.Search
            allowClear
            placeholder="Number or phone"
            style={{ width: 230 }}
            value={search}
            onChange={e => setSearch(e.target.value)}
            onSearch={applyFilters}
          />
          <Button type="primary" onClick={applyFilters}>
            Apply
          </Button>
          <Button onClick={clearFilters}>Clear</Button>
        </Space>

        <Table
          rowKey="id"
          dataSource={rows}
          columns={shipmentColumns()}
          pagination={false}
          scroll={{ x: 1100 }}
          size="middle"
          locale={{ emptyText: empty }}
          onRow={r => ({
            // Anywhere but a link or a button: WhatsApp and Call are the reason a row is tapped at
            // all, and they must not also navigate away from the list.
            onClick: e => {
              if (e.target.closest && e.target.closest('a,button')) return
              history.push(`/tracking/shipments/${r.id}`)
            },
            style: { cursor: 'pointer' }
          })}
        />

        {rows.length ? (
          <PaginationContainer meta={meta} getData={url => load(applied, url)} />
        ) : null}
      </Card>
    </PageLoader>
  )
}

ShipmentsList.propTypes = {
  history: PropTypes.object
}

export default withRouter(ShipmentsList)
